
import { Avatar, Button, IconButton, Popover, Typography, Menu, MenuItem } from "@mui/material";
import { Box } from "@mui/system";
import logo from "../Assets/SmartKart_Logo_Final_SmartKart_Color.png";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from 'primereact/badge';
import { updateOrderDetails, updateOrderModel, updateselectedDeliveryPartner } from "../Redux/orders";
import { Dropdown } from "primereact/dropdown";
import { useGetInventory, useGetCampaign, useGetOffers, useFetchOrders, useGetProducts } from "./globalFunctions";
import { updateSelectedHub } from "../Redux/user";
import { updatepageName } from "../Redux/page";
import { updateInventoryDetailsLoading, updateallInventryDetails } from "../Redux/inventory";
import dayjs from "dayjs";
import {useGetProductBanners} from '../Components/globalFunctions'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CustomAxios from "../utils/CustomAxios";

export function Header() {
    const first_name = useSelector(state => state.user.first_name);
    const last_name = useSelector(state => state.user.last_name);
    const dispatch = useDispatch();
    const pending = useSelector(state => state.orders.pendingOrders);
    const socketStatus = useSelector(state => state.page.socketStatus);
    const inventory = useSelector(state => state.inventory.inventory);
    const allInventoryDetails = useSelector(state => state.inventory.allInventryDetails);
    const hub = useSelector(state => state.user.selected_hub);
    const [filteredInventory, setFilteredInventory] = useState([]);
    const [anchorElNotify, setAnchorElNotify] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const orderDate = useSelector(state => state.orders.selectedDate)
    const user_role = localStorage.getItem('role');
    const user_name = localStorage.getItem('first_name');
    const today = dayjs()
    const navigate = useNavigate();
    const getCampaign = useGetCampaign()
    const getInventory = useGetInventory();
    const getOffers = useGetOffers()
    const GetOrders = useFetchOrders()
    const getDetails = useGetProducts()
    const getProductBanner = useGetProductBanners()



    // Resolve selected hub id consistently (store can contain number or [number])
    const resolvedHubId = useMemo(() => {
        const normalize = (value) => {
            if (value === null || value === undefined) return null;
            if (Array.isArray(value)) return normalize(value[0]);
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        };

        const fromStore = normalize(hub);
        if (fromStore) return fromStore;

        const stored = localStorage.getItem("selected_hub");
        if (!stored) return null;
        try {
            const parsed = JSON.parse(stored);
            return normalize(parsed);
        } catch (e) {
            return normalize(stored);
        }
    }, [hub]);

    // Prefetch inventory details for the selected hub so low-stock alert works globally (not only on Inventory page)
    useEffect(() => {
        let cancelled = false;

        const fetchInventoryDetailsForHub = async () => {
            if (!resolvedHubId) return;

            dispatch(updateInventoryDetailsLoading(true));
            try {
                const res = await CustomAxios.get(`inventory/details/view/${resolvedHubId}/`);
                const options = res?.data?.map((item) => ({
                    ...item,
                    details_id: item.details,
                })) || [];
                if (!cancelled) {
                    dispatch(updateallInventryDetails(options));
                }
            } catch (error) {
                console.error("Error fetching inventory details (header prefetch):", error);
            } finally {
                if (!cancelled) {
                    dispatch(updateInventoryDetailsLoading(false));
                }
            }
        };

        fetchInventoryDetailsForHub();
        return () => { cancelled = true; };
    }, [dispatch, resolvedHubId]);

    // Low-stock alert count (product-level) computed from currently loaded inventory details.
    // Rule: sum Capacity In Stock (available_units) per product_id; if 0 < total <= 20 => low stock.
    const lowStockAlertCount = useMemo(() => {
        if (!Array.isArray(allInventoryDetails) || allInventoryDetails.length === 0) return 0;

        const toUnits = (v) => {
            const n = Number(v);
            return Number.isFinite(n) && n >= 0 ? n : null;
        };

        const totalsByProduct = new Map();
        for (const row of allInventoryDetails) {
            const productId = row?.product_id;
            if (!productId) continue;

            const units = toUnits(row?.available_units);
            if (units === null) continue;

            totalsByProduct.set(productId, (totalsByProduct.get(productId) || 0) + units);
        }

        let count = 0;
        for (const total of totalsByProduct.values()) {
            if (total > 0 && total <= 5) count += 1;
        }
        return count;
    }, [allInventoryDetails]);

    const handleLowStockAlertClick = () => {
        dispatch(updatepageName("inventory"));
        navigate("/home/inventory");
    };


    const handleClick = (event) => setAnchorElNotify(event.currentTarget);
    const handleCloseNotify = () => setAnchorElNotify(null);

    const capitalize = (str) => str?.split(' ').map(word => word?.charAt(0).toUpperCase() + word.slice(1)).join(' ');

    const getRoleColors = (role) => {
        switch (role) {
            case 'super_admin':
                return {
                    bg: 'rgba(220, 38, 127, 0.1)', 
                    border: 'rgba(220, 38, 127, 0.2)',
                    nameColor: '#DC267F',
                    roleColor: '#BE185D' 
                };
            case 'admin':
                return {
                    bg: 'rgba(59, 130, 246, 0.1)', 
                    border: 'rgba(59, 130, 246, 0.2)', 
                    nameColor: '#3B82F6', 
                    roleColor: '#1D4ED8'
                };
            case 'supervisor':
                return {
                    bg: 'rgba(16, 185, 129, 0.1)', 
                    border: 'rgba(16, 185, 129, 0.2)', 
                    nameColor: '#10B981', 
                    roleColor: '#059669'
                };
            case 'customer_engagement_admin':
                return {
                    bg: 'rgba(245, 158, 11, 0.1)', 
                    border: 'rgba(245, 158, 11, 0.2)', 
                    nameColor: '#F59E0B', 
                    roleColor: '#D97706' 
                };
            default:
                return {
                    bg: 'rgba(107, 114, 128, 0.1)', 
                    border: 'rgba(107, 114, 128, 0.2)', 
                    nameColor: '#6B7280', 
                    roleColor: '#4B5563' 
                };
        }
    };

    const roleColors = getRoleColors(user_role);

    const getHubName = (hubId) => {
        const matched = inventory?.find((inv) => inv.value === hubId);
        return matched ? matched.name : null;
    };

    const [selectedHub, setSelectedHub] = useState(null);

    // const handleChange = (e) => {
    //     const selectedHub = e.value;
    //     dispatch(updateSelectedHub([selectedHub]));
    //     localStorage.setItem('selected_hub', selectedHub);
    // };
    const handleChange = (e) => {
        const selectedHub = e.value;
        setSelectedHub(selectedHub); // <-- important
        dispatch(updateSelectedHub([selectedHub]));
        localStorage.setItem('selected_hub', selectedHub);
        
        // Store franchise ID for the selected hub
        const selectedInventory = inventory.find(inv => inv.value === selectedHub);
        console.log('🍽️ Header: Selected hub:', selectedHub);
        console.log('🍽️ Header: Selected inventory:', selectedInventory);
        console.log('🍽️ Header: All inventory data:', inventory);
        
        if (selectedInventory && selectedInventory.franchise) {
            const franchiseId = selectedInventory.franchise.id || selectedInventory.franchise;
            console.log('🍽️ Header: Selected hub franchise ID:', franchiseId);
            localStorage.setItem('selected_franchise_id', franchiseId.toString());
        } else {
            console.log('🍽️ Header: No franchise found for selected hub');
            localStorage.removeItem('selected_franchise_id');
        }
    };

    useEffect(() => {
        dispatch(updateSelectedHub([selectedHub]));
    }, [selectedHub])

    useEffect(() => {
        if (inventory && inventory.length > 0 && selectedHub) {
            const hubExists = inventory.some(inv => inv.value === selectedHub);
            if (!hubExists) {
                const firstHub = inventory[0];
                setSelectedHub(firstHub.value);
                localStorage.setItem('selected_hub', firstHub.value.toString());
                dispatch(updateSelectedHub([firstHub.value]));
                
                // Store franchise ID for the automatically selected hub
                if (firstHub.franchise) {
                    const franchiseId = firstHub.franchise.id || firstHub.franchise;
                    console.log('🍽️ Header: Auto-selected hub franchise ID:', franchiseId);
                    localStorage.setItem('selected_franchise_id', franchiseId.toString());
                }
            }
        }
    }, [inventory, selectedHub]);

    // Effect to store franchise ID when inventory data is loaded and hub is already selected
    useEffect(() => {
        if (inventory && inventory.length > 0 && selectedHub) {
            const selectedInventory = inventory.find(inv => inv.value === selectedHub);
            console.log('🍽️ Header: Initial hub check - Selected hub:', selectedHub);
            console.log('🍽️ Header: Initial hub check - Selected inventory:', selectedInventory);
            
            if (selectedInventory && selectedInventory.franchise) {
                const franchiseId = selectedInventory.franchise.id || selectedInventory.franchise;
                console.log('🍽️ Header: Initial hub franchise ID:', franchiseId);
                localStorage.setItem('selected_franchise_id', franchiseId.toString());
            } else {
                console.log('🍽️ Header: No franchise found for initial hub');
                localStorage.removeItem('selected_franchise_id');
            }
        }
    }, [inventory]); // Run when inventory data is loaded


    // const total_hub = JSON.parse(localStorage.getItem('hub'));

    useEffect(() => {
        try {
            const total_hub = JSON.parse(localStorage.getItem('hub') || '[]');
            const user_role = localStorage.getItem('role');
            const currentSelectedHubRaw = localStorage.getItem('selected_hub');
            let currentSelectedNumeric = null;
            try {
                const parsedSel = JSON.parse(currentSelectedHubRaw || 'null');
                currentSelectedNumeric = Array.isArray(parsedSel) ? (parsedSel[0] ?? null) : (parsedSel ?? null);
            } catch (e) {
                currentSelectedNumeric = currentSelectedHubRaw ? Number(currentSelectedHubRaw) : null;
            }

            let nextFiltered = [];
            if (user_role === 'super_admin') {
                nextFiltered = inventory;
            } else if (['supervisor', 'admin', 'customer_engagement_admin'].includes(user_role)) {
                if (Array.isArray(total_hub) && total_hub.length > 0) {
                    nextFiltered = inventory.filter(item => total_hub.includes(item.value));
                } else if (currentSelectedNumeric) {
                    // No assigned hubs array → restrict to currently selected hub only
                    nextFiltered = inventory.filter(item => item.value === Number(currentSelectedNumeric));
                } else {
                    nextFiltered = [];
                }
            } else {
                nextFiltered = inventory.filter(item => total_hub.includes(item.value));
            }

            console.log('Header hub options (filtered):', nextFiltered);
            setFilteredInventory(nextFiltered);

            if (nextFiltered.length > 0) {
                const currentSelectedHub = localStorage.getItem('selected_hub');
                if (!currentSelectedHub || currentSelectedHub === '[]' || currentSelectedHub === '') {
                    const firstHub = nextFiltered[0];
                    localStorage.setItem('selected_hub', firstHub.value.toString());
                    dispatch(updateSelectedHub([firstHub.value]));
                    setSelectedHub(firstHub.value);
                } else {
                    try {
                        const parsedHub = JSON.parse(currentSelectedHub);
                        const selectedValue = Array.isArray(parsedHub) && parsedHub.length > 0 ? parsedHub[0] : Number(currentSelectedHub);
                        const existsInFiltered = nextFiltered.some(inv => inv.value === Number(selectedValue));
                        if (existsInFiltered) {
                            setSelectedHub(Number(selectedValue));
                        } else {
                            const firstHub = nextFiltered[0];
                            localStorage.setItem('selected_hub', firstHub.value.toString());
                            dispatch(updateSelectedHub([firstHub.value]));
                            setSelectedHub(firstHub.value);
                        }
                    } catch (error) {
                        const selectedValue = Number(currentSelectedHub);
                        const existsInFiltered = nextFiltered.some(inv => inv.value === selectedValue);
                        setSelectedHub(existsInFiltered ? selectedValue : nextFiltered[0].value);
                        if (!existsInFiltered) {
                            localStorage.setItem('selected_hub', nextFiltered[0].value.toString());
                            dispatch(updateSelectedHub([nextFiltered[0].value]));
                        }
                    }
                }
            } else {
                setSelectedHub(null);
            }
        } catch (error) {
            console.error("Error parsing hub from localStorage:", error);
            setFilteredInventory([]);
        }
    }, [inventory]);

    useEffect(() => {
        getInventory();

        const storedHub = localStorage.getItem('hub');
        const selectedHubFromStorage = localStorage.getItem('selected_hub');
        const user_role = localStorage.getItem('role');

        let parsedHub = [];

        if (storedHub) {
            try {
                parsedHub = JSON.parse(storedHub);
            } catch (error) {
                // Fallback for old format
                parsedHub = storedHub.includes(',')
                    ? storedHub.split(',').map(Number)
                    : [Number(storedHub)];
            }
        }

        if (parsedHub.length > 0) {
            dispatch(updateSelectedHub(parsedHub));
        }

        if (selectedHubFromStorage) {
            try {
                const parsedSelectedHub = JSON.parse(selectedHubFromStorage);
                if (Array.isArray(parsedSelectedHub) && parsedSelectedHub.length > 0) {
                    setSelectedHub(parsedSelectedHub[0]);
                    dispatch(updateSelectedHub(parsedSelectedHub));
                } else {
                    setSelectedHub(Number(selectedHubFromStorage));
                    dispatch(updateSelectedHub([Number(selectedHubFromStorage)]));
                }
            } catch (error) {
                const hubValue = Number(selectedHubFromStorage);
                setSelectedHub(hubValue);
                dispatch(updateSelectedHub([hubValue]));
            }
        }
    }, []);


    function stringToColor(string) {
        let hash = 0;
        for (let i = 0; i < string.length; i++) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }

        let color = '#';
        for (let i = 0; i < 3; i++) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }
        return color;
    }

    function stringAvatar(name) {
        return {
            sx: {
                bgcolor: stringToColor(name),
            },
            children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
        };
    }

    const logout = () => {
        localStorage.clear();
        navigate("/");
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleModalChange = (item) => {
        handleCloseNotify();
        dispatch(updateOrderModel(true));
        dispatch(updateOrderDetails(item));
        if (item.delivery_partner_id) {
            dispatch(updateselectedDeliveryPartner(item.delivery_partner_id));
        } else {
            dispatch(updateselectedDeliveryPartner(""));
        }
    };



    useEffect(() => {
        getInventory();
        getCampaign()
        getOffers()
        getDetails()
        getProductBanner()
        orderDate != null && GetOrders(orderDate[0], orderDate[1])
    }, [hub]);

    return (
        <Box sx={{ 
            width: "100%", 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            backgroundColor: "background.light",
            flexWrap: { xs: "wrap", md: "nowrap" },
            minHeight: { xs: "auto", md: "72px" }
        }}>
            <Box sx={{ 
                display: "flex", 
                gap: { xs: "8px", md: "12px" }, 
                alignItems: "flex-end", 
                padding: { xs: "8px 12px", md: "20px" },
                flexShrink: 0
            }}>
                <Box component="img" sx={{ width: { xs: "28px", md: "38px" }, height: { xs: "24px", md: "32px" } }} src={logo} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "4px", md: "8px" } }}>
                    <Typography sx={{ 
                        lineHeight: { xs: "8px", md: "10px" }, 
                        fontSize: { xs: "12px", md: "18px" }, 
                        fontWeight: "600" 
                    }}>
                        SMARTKART
                    </Typography>
                    <Typography sx={{ 
                        lineHeight: { xs: "8px", md: "10px" }, 
                        fontSize: { xs: "10px", md: "18px" }, 
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                    }}>
                        CONTROL HUB {socketStatus ? <Badge severity="success" /> : <Badge severity="danger" />}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ 
                width: { xs: "100%", md: "100%" }, 
                maxWidth: { xs: "100%", md: "400px" }, 
                display: "flex", 
                justifyContent: { xs: "space-between", md: "flex-end" }, 
                gap: { xs: "8px", md: "14px" }, 
                alignItems: "center", 
                padding: { xs: "8px 12px", md: "20px" },
                paddingRight: { xs: "12px", md: "20px" },
                flexWrap: { xs: "wrap", md: "nowrap" }
            }}>
                 <Box sx={{ 
                    width: { xs: "140px", sm: "180px", md: "240px" },
                    "& .p-dropdown": {
                        height: { xs: "28px", md: "32px" },
                        fontSize: { xs: "12px", md: "14px" }
                    }
                }}>
                <Dropdown
                    onChange={handleChange}
                    value={selectedHub}
                    options={filteredInventory}
                    name="details_id"
                    optionLabel="name"
                    style={{ zIndex: "1000", height: "32px", width: "240px" }}
                    placeholder="Select the hub"
                    className="w-full md:w-14rem"
                    checkmark
                    highlightOnSelect={false}
                />
                </Box>
                {/* User Info Label */}
                <Box sx={{ 
                    display: { xs: "none", md: "flex" }, 
                    flexDirection: "column", 
                    alignItems: "center",
                    padding: "4px 12px",
                    backgroundColor: roleColors.bg,
                    borderRadius: "8px",
                    border: `1px solid ${roleColors.border}`,
                    minWidth: "120px"
                }}>
                    <Typography sx={{ 
                        fontSize: "12px", 
                        fontWeight: "600", 
                        color: roleColors.nameColor,
                        lineHeight: "14px"
                    }}>
                        {user_name || 'User'}
                    </Typography>
                    <Typography sx={{ 
                        fontSize: "10px", 
                        fontWeight: "500", 
                        color: roleColors.roleColor,
                        lineHeight: "12px",
                        textTransform: "capitalize"
                    }}>
                        {user_role?.replace('_', ' ') || 'Role'}
                    </Typography>
                </Box>

                {/* Low Stock Alerts */}
                {lowStockAlertCount > 0 && (
                    <IconButton
                        aria-label="Low stock alerts"
                        onClick={handleLowStockAlertClick}
                        sx={{
                            padding: { xs: "4px", md: "8px" },
                            "& .pi": {
                                fontSize: { xs: "16px", md: "20px" },
                                color: "#dc3545"
                            }
                        }}
                    >
                        <i className="pi pi-exclamation-triangle p-overlay-badge">
                            <Badge value={lowStockAlertCount} severity="danger" />
                        </i>
                    </IconButton>
                )}

                <IconButton 
                    disabled={pending.length > 0 ? false : true} 
                    aria-describedby="simple-popover" 
                    onClick={handleClick}
                    sx={{ 
                        padding: { xs: "4px", md: "8px" },
                        "& .pi": { 
                            fontSize: { xs: "16px", md: "20px" } 
                        }
                    }}
                >
                    <i className="pi pi-bell p-overlay-badge">
                        {pending.length > 0 && <Badge value={pending.length} />}
                    </i>
                </IconButton>
                {/* Mobile User Info Label */}
                <Box sx={{ 
                    display: { xs: "flex", md: "none" }, 
                    flexDirection: "column", 
                    alignItems: "center",
                    padding: "2px 8px",
                    backgroundColor: roleColors.bg,
                    borderRadius: "6px",
                    border: `1px solid ${roleColors.border}`,
                    minWidth: "80px"
                }}>
                    <Typography sx={{ 
                        fontSize: "10px", 
                        fontWeight: "600", 
                        color: roleColors.nameColor,
                        lineHeight: "12px"
                    }}>
                        {user_name || 'User'}
                    </Typography>
                    <Typography sx={{ 
                        fontSize: "8px", 
                        fontWeight: "500", 
                        color: roleColors.roleColor,
                        lineHeight: "10px",
                        textTransform: "capitalize"
                    }}>
                        {user_role?.replace('_', ' ') || 'Role'}
                    </Typography>
                </Box>

                <Box sx={{ 
                    width: { xs: "auto", md: "50px" }, 
                    maxWidth: { xs: "none", md: "50px" }, 
                    display: "flex", 
                    justifyContent: "flex-end", 
                    gap: "24px", 
                    alignItems: "center",
                    flexShrink: 0
                }}>
                    <Avatar 
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                        sx={{ 
                            height: { xs: "28px", md: "32px" }, 
                            width: { xs: "28px", md: "32px" }, 
                            borderRadius: "50%", 
                            bgcolor: "#3B82F6", 
                            fontSize: { xs: "12px", md: "14px" }, 
                            fontWeight: "700", 
                            cursor: "pointer"
                        }}
                    >
                        <PersonOutlineOutlinedIcon sx={{ fontSize: { xs: "16px", md: "20px" } }} />
                    </Avatar>
                    {/* <Avatar {...stringAvatar(capitalize(first_name) + " " + capitalize(last_name))} onClick={(event) => setAnchorEl(event.currentTarget)} style={{ height: "32px", width: "32px", borderRadius: "16px", bgcolor: "#3B82F6", fontSize: "14px", fontWeight: "700", cursor: "pointer" }} /> */}
                </Box>
            </Box>
            <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{ padding: 0 }}
            >
                <MenuItem sx={{ backgroundColor: 'white', padding: "5px 15px" }} disabled={true} >{user_name} - {user_role}</MenuItem>
                <MenuItem sx={{ backgroundColor: 'white', padding: "5px 15px" }} onClick={() => logout()}>Logout</MenuItem>
            </Menu>
            <Popover 
                id="simple-popover" 
                open={Boolean(anchorElNotify)} 
                anchorEl={anchorElNotify} 
                onClose={handleCloseNotify} 
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                sx={{ 
                    maxWidth: { xs: "90vw", md: "400px" },
                    width: { xs: "90vw", md: "400px" }
                }}
            >                
            {pending?.length > 0 && pending?.map((item) => (
                   <Box sx={{ 
                    width: { xs: "90vw", md: "400px" }, 
                    maxWidth: { xs: "90vw", md: "400px" },
                    height: "100%", 
                    minHeight: "100px", 
                    backgroundColor: 'white', 
                    border: "1px", 
                    borderColor: "secondary.border", 
                    padding: { xs: "8px", md: "10px" }
                }}>
                    <Box sx={{ padding: { xs: "8px", md: "10px" }, backgroundColor: "background.default", borderRadius: "5px" }}>
                        <Typography sx={{ fontWeight: 500, fontSize: { xs: "12px", md: "14px" } }}>Order Id: {item.order_id}</Typography>
                        <Box sx={{ display: "flex", gap: { xs: "6px", md: "10px" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, mt: { xs: "8px", md: "4px" } }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: { xs: "12px", md: "14px" }, fontWeight: "400", lineHeight: { xs: "18px", md: "22px" } }}>
                                    {item?.delivery_address?.door_flat_no}, {item?.delivery_address?.address}
                                </Typography>
                                </Box>
                                <Button 
                                    variant="contained" 
                                    sx={{ 
                                        height: { xs: "28px", md: "32px" },
                                        fontSize: { xs: "11px", md: "14px" },
                                        minWidth: { xs: "60px", md: "auto" }
                                    }} 
                                    onClick={() => handleModalChange(item)}
                                >
                                    View
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Popover>
        </Box>
    );
}
